import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Select,
  Chip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { currencyFormatter, percentFormatter, decimalFormatter } from '../../util/formatHelper';
import useDiscountCodeStatistics from '../../hooks/useDiscountCodeStatistics';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import useBranch from '../../hooks/useBranch';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getRechartsTooltipLabelStyle,
  getStatisticsAltSurfaceBackground,
  getStatisticsChartCursorFill,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getStatisticsTotalsHtmlCellStyle,
  getRechartsLegendBarProps,
  getRechartsLegendPieProps,
  getStatisticsHtmlThStyle,
  getStatisticsPageRootStyle,
} from '../../util/chartTheme';
import { STATISTIC_TYPE_DISCOUNT_CODE } from '../../constants/statisticTypes';

dayjs.extend(isoWeek);

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const defaultStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const formatCurrency = value => currencyFormatter.format(value ?? 0);

const CHANNEL_LABELS = {
  webshop: 'Webshop',
  callcenter: 'Call Center',
  'on-site': 'Vor Ort',
  backend: 'Backend',
  unknown: 'Unbekannt',
};

const CATEGORY_LABELS = {
  booking: 'Buchung',
  package: 'Paket',
  upsell: 'Upsell',
  surcharge: 'Aufschlag',
  special: 'Spezial',
  resourceVoucher: 'Personen-Gutschein',
  voucherRedemption: 'Gutschein-Einlösung',
  offlinePaymentFee: 'Offline-Zahlungsgebühr',
  unknown: 'Unbekannt',
};

const DiscountCodeStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const altSurfaceBg = useMemo(() => getStatisticsAltSurfaceBackground(theme), [theme]);
  const chartCursorFill = useMemo(() => getStatisticsChartCursorFill(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const tooltipLabelStyle = useMemo(() => getRechartsTooltipLabelStyle(theme), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const channelColors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
    ],
    [theme]
  );
  const savingsChartColors = useMemo(
    () => [theme.palette.success.main, theme.palette.error.main],
    [theme]
  );
  const secondaryTextColor = theme.palette.text.secondary;
  const totalsHtmlCell = useCallback(
    (extra = {}) => getStatisticsTotalsHtmlCellStyle(theme, extra),
    [theme]
  );
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const cardStyle = useMemo(
    () => ({
      background: statSurfaceBg,
      borderRadius: '8px',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    }),
    [statSurfaceBg]
  );
  const authCtx = useContext(AuthContext);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    discountCodeId: '',
    branchId: '',
  });
  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [selectedCodeId, setSelectedCodeId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [selectedCacheEntryId, setSelectedCacheEntryId] = useState(null);

  const { downloadDiscountCodeStatisticsReport, downloadDiscountCodeStatisticsExcel } =
    useDiscountCodeStatistics();
  const { getBranches } = useBranch();

  const getBranchesRef = useRef(getBranches);

  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const data = await getBranchesRef.current();
        setBranches(data || []);
      } catch (error) {
        toast.error('Fehler beim Laden der Standorte');
      }
    };
    loadBranches();
  }, []);

  const applyFilters = (nextStart, nextEnd) => {
    const s = nextStart || startDate;
    const e = nextEnd || endDate;
    if (!s || !e || !s.isValid() || !e.isValid()) {
      toast.error('Bitte wähle gültige Daten aus');
      return;
    }

    if (s.isAfter(e)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }

    const newFilter = {
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      discountCodeId: selectedCodeId,
      branchId: selectedBranchId,
    };

    setSelectedCacheEntryId(null);
    setAppliedFilter(newFilter);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilters();
    }
  };

  const filterParams = useMemo(() => {
    const p = {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branchScope: appliedFilter.branchId || 'all',
      discountCodeId: appliedFilter.discountCodeId || 'all',
    };
    if (appliedFilter.branchId) p.branchId = appliedFilter.branchId;
    return p;
  }, [appliedFilter]);

  const statisticJob = useStatisticJob({
    statisticType: STATISTIC_TYPE_DISCOUNT_CODE,
    endpoint: '/api/v1/discountCode/statistics',
    filterParams,
  });

  const stats = statisticJob.payload;
  const isFetchingStats =
    !stats && (statisticJob.status === 'idle' || statisticJob.status === 'fetching');

  useEffect(() => {
    if (statisticJob.status === 'failed' && statisticJob.error) {
      toast.error(`Fehler beim Laden der Rabattcode-Statistiken: ${statisticJob.error}`);
    }
  }, [statisticJob.status, statisticJob.error]);

  useEffect(() => {
    if (stats?.topCodes && discountCodes.length === 0) {
      setDiscountCodes(
        stats.topCodes.map(c => ({ _id: c.discountCodeId, code: c.code }))
      );
    }
  }, [stats, discountCodes.length]);

  const { entries: cacheEntries, isLoading: cacheEntriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_DISCOUNT_CODE,
  });

  const handleSelectCacheEntry = useCallback(
    entryId => {
      setSelectedCacheEntryId(entryId);
      if (entryId) {
        statisticJob.loadCacheEntry(entryId);
      } else {
        statisticJob.fetch();
      }
    },
    [statisticJob]
  );

  const downloadReport = useCallback(async () => {
    setIsDownloadingReport(true);
    try {
      const blob = await downloadDiscountCodeStatisticsReport(
        appliedFilter.startDate,
        appliedFilter.endDate,
        appliedFilter.discountCodeId || null,
        appliedFilter.branchId || null
      );

      if (!blob) return;

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `discount-code-report-${appliedFilter.startDate}_${appliedFilter.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Fehler beim Erstellen des Reports');
    } finally {
      setIsDownloadingReport(false);
    }
  }, [appliedFilter, downloadDiscountCodeStatisticsReport]);

  const downloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const blob = await downloadDiscountCodeStatisticsExcel(
        appliedFilter.startDate,
        appliedFilter.endDate,
        appliedFilter.discountCodeId || null,
        appliedFilter.branchId || null
      );

      if (!blob) return;

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `rabattcode-auswertung-${appliedFilter.startDate}_${appliedFilter.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Fehler beim Erstellen der Excel-Datei');
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [appliedFilter, downloadDiscountCodeStatisticsExcel]);

  useEffect(() => {
    setStartDate(dayjs(appliedFilter.startDate));
    setEndDate(dayjs(appliedFilter.endDate));
    setSelectedCodeId(appliedFilter.discountCodeId || '');
    setSelectedBranchId(appliedFilter.branchId || '');
  }, [appliedFilter]);

  const chartData = useMemo(() => {
    if (!stats?.timeSeries?.daily) return [];

    const dailyData = stats.timeSeries.daily.filter(Boolean);

    if (chartType === 'line') {
      return dailyData.map(point => ({
        ...point,
        label: dayjs(point.date).format('DD.MM.'),
      }));
    }

    if (!stats.appliedFilter) return [];
    const start = dayjs(stats.appliedFilter.startDate);
    const end = dayjs(stats.appliedFilter.endDate);
    const diffDays = end.diff(start, 'day') + 1;

    let granularity = 'day';
    if (diffDays > 180) granularity = 'month';
    else if (diffDays > 35) granularity = 'week';

    if (granularity === 'day') {
      return dailyData.map(point => ({
        ...point,
        label: dayjs(point.date).format('DD.MM.'),
      }));
    }

    const aggregated = {};
    dailyData.forEach(point => {
      const date = dayjs(point.date);
      let key;
      let label;

      if (granularity === 'week') {
        const weekStart = date.startOf('isoWeek');
        const weekEnd = date.endOf('isoWeek');
        key = weekStart.format('YYYY-MM-DD');
        label = `${weekStart.format('DD.MM.')} – ${weekEnd.format('DD.MM.')}`;
      } else {
        key = date.format('YYYY-MM');
        label = date.format('MMM YYYY');
      }

      if (!aggregated[key]) {
        aggregated[key] = {
          date: key,
          label,
          redemptionCount: 0,
          amountSaved: 0,
          bookingRevenue: 0,
        };
      }

      aggregated[key].redemptionCount += point.redemptionCount || 0;
      aggregated[key].amountSaved += point.amountSaved || 0;
      aggregated[key].bookingRevenue += point.bookingRevenue || 0;
    });

    return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));
  }, [stats, chartType]);

  const hasChartData = chartData.length > 0;

  const handleChartTypeChange = (_, value) => {
    if (value) setChartType(value);
  };

  const cards = useMemo(() => {
    if (!stats) return [];
    return [
      {
        label: 'Einlösungen',
        value: stats.totalRedemptions,
        helper: 'Buchungen mit Rabattcode im Zeitraum',
      },
      {
        label: 'Gesamtersparnis',
        value: formatCurrency(stats.totalAmountSaved),
        helper: 'Summe aller gewährten Rabatte',
      },
      {
        label: 'Buchungsumsatz',
        value: formatCurrency(stats.totalBookingRevenue),
        helper: 'Umsatz der Rabattcode-Buchungen',
      },
      {
        label: 'Effektiver Rabattanteil',
        value: stats.effectiveDiscountRate
          ? `${percentFormatter.format(stats.effectiveDiscountRate)} %`
          : '0,0 %',
        helper: 'Anteil der Rabatte am Gesamtpreis',
      },
      {
        label: 'Ø Ersparnis / Einlösung',
        value: formatCurrency(stats.averageSavedPerRedemption),
        helper: 'Durchschnittliche Ersparnis pro Buchung',
      },
      {
        label: 'Ø Rabatt-Prozent',
        value: stats.averageDiscountPercent
          ? `${percentFormatter.format(stats.averageDiscountPercent)} %`
          : '0,0 %',
        helper: 'Durchschnittlicher globalPercent der Codes',
      },
      {
        label: 'Codes gesamt',
        value: stats.totalDiscountCodes,
        helper: `Davon aktiv: ${stats.totalActiveDiscountCodes}`,
      },
      {
        label: 'Genutzte Codes',
        value: stats.codesUsedInPeriod,
        helper: 'Codes mit mind. 1 Einlösung im Zeitraum',
      },
    ];
  }, [stats]);

  const channelPieData = useMemo(() => {
    if (!stats?.channelBreakdown || stats.channelBreakdown.length === 0) return [];
    return stats.channelBreakdown.map(ch => ({
      name: CHANNEL_LABELS[ch.channel] || ch.channel,
      value: ch.redemptionCount,
    }));
  }, [stats]);

  if (!authCtx.checkRight('discountCode:special:statistics')) {
    return <Navigate to='/welcome' replace />;
  }

  const branchOptionsForShell = useMemo(
    () => [
      { _id: '', label: 'Alle Standorte' },
      ...branches.map(b => ({ _id: b._id, label: b.locationName })),
    ],
    [branches]
  );

  const discountCodeFilterParamLookups = useMemo(() => {
    const codeMap = {};
    for (const dc of discountCodes) codeMap[String(dc._id)] = dc.code;
    return { discountCodeId: codeMap };
  }, [discountCodes]);

  const discountCodeFilter = (
    <FormControl fullWidth>
      <InputLabel id='discount-code-label'>Rabattcode</InputLabel>
      <Select
        labelId='discount-code-label'
        value={selectedCodeId}
        label='Rabattcode'
        onChange={event => setSelectedCodeId(event.target.value)}
        onKeyDown={handleKeyDown}
      >
        <MenuItem value=''>Alle Codes</MenuItem>
        {discountCodes.map(dc => (
          <MenuItem key={dc._id} value={dc._id}>
            {dc.code}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const downloadButtons = (
    <>
      <Button
        variant='outlined'
        onClick={downloadExcel}
        disabled={isDownloadingExcel}
        aria-label='Excel herunterladen'
        sx={{
          minWidth: 56,
          minHeight: 56,
          padding: 0,
          borderRadius: 2,
        }}
        title={isDownloadingExcel ? 'Excel wird erstellt …' : 'Excel herunterladen'}
      >
        {isDownloadingExcel ? (
          <CircularProgress size={18} color='inherit' />
        ) : (
          <TableChartIcon fontSize='small' />
        )}
      </Button>
      <Button
        variant='outlined'
        onClick={downloadReport}
        disabled={isDownloadingReport}
        aria-label='PDF herunterladen'
        sx={{
          minWidth: 56,
          minHeight: 56,
          padding: 0,
          borderRadius: 2,
        }}
        title={isDownloadingReport ? 'PDF wird erstellt …' : 'PDF herunterladen'}
      >
        {isDownloadingReport ? (
          <CircularProgress size={18} color='inherit' />
        ) : (
          <PictureAsPdfIcon fontSize='small' />
        )}
      </Button>
    </>
  );

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      branchOptions={branchOptionsForShell}
      selectedBranchId={selectedBranchId}
      onBranchChange={setSelectedBranchId}
      branchSelectIdPrefix='discount-code-branch'
      extraFilters={discountCodeFilter}
      onApply={applyFilters}
      applyExtraActions={downloadButtons}
      status={statisticJob.status}
      isStale={statisticJob.isStale}
      cacheComputedAt={statisticJob.cacheComputedAt}
      cacheExpiresAt={statisticJob.cacheExpiresAt}
      job={statisticJob.job}
      error={statisticJob.error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedCacheEntryId}
      onSelectEntry={handleSelectCacheEntry}
      entriesLoading={cacheEntriesLoading}
      onRefresh={statisticJob.refresh}
      filterParamLookups={discountCodeFilterParamLookups}
      hasPayload={!!stats}
    >
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Kennzahlen
        </Typography>
        {stats && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
            }}
          >
            {cards.map(card => (
              <article key={card.label} style={cardStyle}>
                <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
                <strong style={{ fontSize: '1.5rem' }}>{card.value}</strong>
                <small style={{ color: kpiHelperColor }}>{card.helper}</small>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Time Series Chart */}
      {stats && (
        <section>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='h6' component='h2' sx={{ m: 0 }}>
              Rabattcode-Nutzung im Zeitverlauf
            </Typography>
            <ToggleButtonGroup
              value={chartType}
              exclusive
              size='small'
              onChange={handleChartTypeChange}
            >
              <ToggleButton value='line'>Linie</ToggleButton>
              <ToggleButton value='bar'>Balken</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {hasChartData ? (
            <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 360 }}>
              <ResponsiveContainer width='100%' height='100%'>
                {chartType === 'line' ? (
                  <LineChart data={chartData}>
                    <CartesianGrid stroke={chartGridStroke} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis stroke={axisStroke} />
                    <Tooltip
                      contentStyle={chartTooltipContentStyle}
                      labelStyle={tooltipLabelStyle}
                      formatter={(value, name) =>
                        name === 'Einlösungen' ? value : formatCurrency(value)
                      }
                    />
                    <Legend {...chartLegendBarProps} />
                    <Line
                      type='monotone'
                      dataKey='amountSaved'
                      name='Ersparnis (€)'
                      stroke={savingsChartColors[1]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type='monotone'
                      dataKey='bookingRevenue'
                      name='Buchungsumsatz (€)'
                      stroke={savingsChartColors[0]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid stroke={chartGridStroke} vertical={false} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis stroke={axisStroke} />
                    <Tooltip
                      cursor={{ fill: chartCursorFill }}
                      contentStyle={chartTooltipContentStyle}
                      labelStyle={tooltipLabelStyle}
                      formatter={(value, name) =>
                        name === 'Einlösungen' ? value : formatCurrency(value)
                      }
                    />
                    <Legend {...chartLegendBarProps} />
                    <Bar dataKey='amountSaved' name='Ersparnis (€)' fill={savingsChartColors[1]} />
                    <Bar
                      dataKey='bookingRevenue'
                      name='Buchungsumsatz (€)'
                      fill={savingsChartColors[0]}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Paper>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Keine Zeitreihendaten verfügbar.
            </Typography>
          )}
        </section>
      )}

      {/* Channel Distribution & Entertainment Breakdown */}
      {stats && (stats.channelBreakdown?.length > 0 || stats.entertainmentBreakdown?.length > 0) && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Verteilung nach Kanal & Entertainment
          </Typography>
          <Grid container spacing={2}>
            {channelPieData.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    background: statSurfaceBg,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 600 }}>
                    Buchungskanäle
                  </Typography>
                  <div style={{ width: '100%', height: 250 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={channelPieData}
                          cx='50%'
                          cy='50%'
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey='value'
                          stroke='none'
                        >
                          {channelPieData.map((_, idx) => (
                            <Cell
                              key={`channel-${idx}`}
                              fill={channelColors[idx % channelColors.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={chartTooltipContentStyle}
                        />
                        <Legend {...chartLegendPieProps} verticalAlign='bottom' height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <Box sx={{ mt: 2, width: '100%' }}>
                    {stats.channelBreakdown.map((ch, idx) => (
                      <div
                        key={ch.channel}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '0.5rem 0',
                          borderBottom:
                            idx < stats.channelBreakdown.length - 1 ? tableRowBorderTop : 'none',
                        }}
                      >
                        <span>{CHANNEL_LABELS[ch.channel] || ch.channel}</span>
                        <strong>
                          {ch.redemptionCount} ({percentFormatter.format(ch.share)} %)
                        </strong>
                      </div>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}

            {stats.entertainmentBreakdown?.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    background: statSurfaceBg,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 600 }}>
                    Entertainments
                  </Typography>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      marginTop: '0.5rem',
                    }}
                  >
                    {stats.entertainmentBreakdown.map(ent => (
                      <div
                        key={ent.entertainmentId}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          background: altSurfaceBg,
                          borderRadius: '6px',
                        }}
                      >
                        <span>{ent.entertainmentName}</span>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                          <Chip
                            label={`${ent.redemptionCount} Einlösungen`}
                            size='small'
                            variant='outlined'
                          />
                          <strong>{formatCurrency(ent.amountSaved)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </Paper>
              </Grid>
            )}
          </Grid>
        </section>
      )}

      {/* Branch Breakdown */}
      {!isFetchingStats && (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Einlösungen nach Filiale
        </Typography>
        {stats?.branchBreakdown?.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '560px',
              }}
            >
              <thead>
                <tr>
                  <th style={htmlThStyle()}>Filiale</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Einlösungen</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Ersparnis</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Buchungsumsatz</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Anteil</th>
                </tr>
              </thead>
              <tbody>
                {stats.branchBreakdown.map(branch => (
                  <tr key={branch.branchId}>
                    <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                      {branch.branchName}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {branch.redemptionCount}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {formatCurrency(branch.amountSaved)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {formatCurrency(branch.bookingRevenue)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {percentFormatter.format(branch.revenueShare)} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Keine Filialdaten verfügbar.
          </Typography>
        )}
      </section>
      )}

      {/* Category Breakdown */}
      {!isFetchingStats && (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Aufschlüsselung nach Kategorie
        </Typography>
        {stats?.categoryBreakdown?.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '620px',
              }}
            >
              <thead>
                <tr>
                  <th style={htmlThStyle()}>Kategorie</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Anzahl</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Preis (netto)</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Rabatt</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>
                    Preis abzgl. Rabatt (netto)
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.categoryBreakdown.map(cat => (
                  <tr key={cat.type}>
                    <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                      {CATEGORY_LABELS[cat.type] || cat.type}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {cat.lineItemCount}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {formatCurrency(cat.totalGross)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                        color: cat.totalDiscount > 0 ? theme.palette.error.main : undefined,
                      }}
                    >
                      {cat.totalDiscount > 0
                        ? `−${formatCurrency(cat.totalDiscount)}`
                        : formatCurrency(0)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                        fontWeight: 600,
                      }}
                    >
                      {formatCurrency(cat.totalNet)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={totalsHtmlCell()}>Gesamt</td>
                  <td style={totalsHtmlCell({ textAlign: 'right' })}>
                    {stats.categoryBreakdown.reduce((sum, c) => sum + c.lineItemCount, 0)}
                  </td>
                  <td style={totalsHtmlCell({ textAlign: 'right' })}>
                    {formatCurrency(
                      stats.categoryBreakdown.reduce((sum, c) => sum + c.totalGross, 0)
                    )}
                  </td>
                  <td
                    style={totalsHtmlCell({
                      textAlign: 'right',
                      color: theme.palette.error.main,
                    })}
                  >
                    −
                    {formatCurrency(
                      stats.categoryBreakdown.reduce((sum, c) => sum + c.totalDiscount, 0)
                    )}
                  </td>
                  <td style={totalsHtmlCell({ textAlign: 'right' })}>
                    {formatCurrency(
                      stats.categoryBreakdown.reduce((sum, c) => sum + c.totalNet, 0)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Keine Kategoriedaten verfügbar.
          </Typography>
        )}
      </section>
      )}

      {/* Top Discount Codes */}
      {!isFetchingStats && (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Top Rabattcodes
        </Typography>
        {stats?.topCodes?.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '780px',
              }}
            >
              <thead>
                <tr>
                  <th style={htmlThStyle()}>Code</th>
                  <th style={htmlThStyle()}>Beschreibung</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Rabatt %</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Einlösungen</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Gesamt / Limit</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Ersparnis</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Buchungsumsatz</th>
                  <th style={htmlThStyle({ textAlign: 'right' })}>Kunden</th>
                  <th style={htmlThStyle({ textAlign: 'center' })}>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.topCodes.map(code => (
                  <tr key={code.discountCodeId}>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        fontFamily: 'monospace',
                        fontWeight: 600,
                      }}
                    >
                      {code.code}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        color: kpiLabelColor,
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {code.description || '–'}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {code.globalPercent} %
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {code.usageCount}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {code.totalUsageCount} / {code.usageLimit ?? '∞'}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {formatCurrency(code.amountSaved)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {formatCurrency(code.bookingRevenue)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'right',
                      }}
                    >
                      {code.uniqueEmailCount}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        borderTop: tableRowBorderTop,
                        textAlign: 'center',
                      }}
                    >
                      <Chip
                        label={code.active ? 'Aktiv' : 'Inaktiv'}
                        size='small'
                        color={code.active ? 'success' : 'default'}
                        variant='outlined'
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Keine Rabattcodes vorhanden.
          </Typography>
        )}
      </section>
      )}

      {/* Report Summary */}
      {stats?.reportSummary && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Report-Zusammenfassung
          </Typography>
          <Paper elevation={3} sx={{ p: 3, background: statSurfaceBg }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <strong>Highlights</strong>
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: secondaryTextColor }}>
                  {stats.reportSummary.highlights.map((item, index) => (
                    <li key={`hl-${index}`}>{item}</li>
                  ))}
                </ul>
              </Grid>
              <Grid item xs={12} md={6}>
                <strong>Insights</strong>
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: secondaryTextColor }}>
                  {stats.reportSummary.insights.map((item, index) => (
                    <li key={`in-${index}`}>{item}</li>
                  ))}
                </ul>
              </Grid>
            </Grid>
          </Paper>
        </section>
      )}
    </StatisticViewShell>
  );
};

export default DiscountCodeStatistics;
